/**
 * AdminUsersScreen — gestión de coaches.
 *
 * Toolbar: search (debounce 300ms), filter por tier, filter por admin/regular.
 * Tabla con paginación 20/página (limit/offset). Click en fila → modal detalle
 * con datos completos, métricas (planes totales, últimos pagos), acciones de
 * cambio de tier / toggle is_admin / cerrar sesión (toast) y export por user
 * (mailto a soporte).
 *
 * NOTA: el filtro is_admin no es parte del backend, lo hacemos client-side
 * sobre la página actual (el backend solo soporta q, tier, active_only).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../lib/toast";
import { Icon } from "../../lib/Icon";
import { Avatar, EmptyState } from "../../lib/components";
import {
  downloadBlob,
  exportCsv,
  getUser,
  listUsers,
  patchUser,
  type AdminUserDetail,
  type AdminUserSummary,
} from "../../api/admin";
import {
  AdminBadge,
  AdminGate,
  Skeleton,
  colorFor,
  formatDate,
  formatDateTime,
  initialsOf,
  relativeTime,
  tierLabel,
} from "./_shared";

const PAGE_SIZE = 20;

export function AdminUsersScreen() {
  return (
    <AdminGate>
      <Inner />
    </AdminGate>
  );
}

type AdminFilter = "all" | "admin" | "regular";

function Inner() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUserSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [adminFilter, setAdminFilter] = useState<AdminFilter>("all");
  const [page, setPage] = useState(0);

  const [openUserId, setOpenUserId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce search 300ms
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await listUsers({
        q: debouncedSearch || undefined,
        tier: tierFilter !== "all" ? tierFilter : undefined,
        // El backend page-1 hipotético: pedimos PAGE_SIZE+1 para saber si hay
        // siguiente página sin endpoint de count.
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
      });
      setUsers(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, tierFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Filter admin client-side sobre la página actual.
  const visible = useMemo(() => {
    const list = (users || []).slice(0, PAGE_SIZE);
    if (adminFilter === "all") return list;
    if (adminFilter === "admin") return list.filter((u) => u.is_admin);
    return list.filter((u) => !u.is_admin);
  }, [users, adminFilter]);

  const hasNextPage = (users?.length || 0) > PAGE_SIZE;

  const onExportAll = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await exportCsv("users");
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `aetherlite-users-${stamp}.csv`);
      toast("CSV de usuarios descargado");
    } catch (e) {
      toast("No pude exportar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setExporting(false);
    }
  }, [exporting, toast]);

  return (
    <div className="page al-admin-shell">
      <div className="al-admin-head">
        <div>
          <h1 className="al-page-title">
            Usuarios <AdminBadge />
          </h1>
          <p className="muted">Coaches registrados en AetherLite.</p>
        </div>
        <button className="btn btn-secondary" onClick={onExportAll} disabled={exporting}>
          <Icon name="upload" size={14} /> {exporting ? "Exportando…" : "Exportar todos (CSV)"}
        </button>
      </div>

      {/* ----- Toolbar ----- */}
      <div className="card al-admin-toolbar">
        <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
          <Icon name="search" size={16} />
          <input
            placeholder="Buscar por email o nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="al-admin-chip-group">
          <span className="tiny muted">Tier:</span>
          {(["all", "free", "solo", "pro", "studio"] as const).map((t) => (
            <button
              key={t}
              className={"chip" + (tierFilter === t ? " is-active" : "")}
              onClick={() => {
                setTierFilter(t);
                setPage(0);
              }}
            >
              {t === "all" ? "Todos" : tierLabel(t)}
            </button>
          ))}
        </div>

        <div className="al-admin-chip-group">
          <span className="tiny muted">Rol:</span>
          {(["all", "admin", "regular"] as const).map((a) => (
            <button
              key={a}
              className={"chip" + (adminFilter === a ? " is-active" : "")}
              onClick={() => setAdminFilter(a)}
            >
              {a === "all" ? "Todos" : a === "admin" ? "Admins" : "Regulares"}
            </button>
          ))}
        </div>
      </div>

      {/* ----- Tabla / cards ----- */}
      <div className="card al-admin-card-flush">
        {loading && !users ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : err && !users ? (
          <EmptyState
            icon="user"
            title="No pude cargar usuarios"
            body={err}
            action={
              <button className="btn btn-primary" onClick={load}>
                Reintentar
              </button>
            }
          />
        ) : visible.length === 0 ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              icon="user"
              title="Sin resultados"
              body={
                debouncedSearch || tierFilter !== "all" || adminFilter !== "all"
                  ? "No hay usuarios con estos filtros. Probá ajustando la búsqueda."
                  : "Todavía no hay coaches registrados en AetherLite."
              }
            />
          </div>
        ) : (
          <>
            <table className="al-admin-table">
              <thead>
                <tr>
                  <th>Coach</th>
                  <th className="hide-mobile">Tier</th>
                  <th className="hide-mobile">Planes</th>
                  <th className="hide-mobile">Registrado</th>
                  <th className="hide-mobile">Últ. pago</th>
                  <th className="hide-mobile">Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr
                    key={u.id}
                    className="al-admin-row"
                    onClick={() => setOpenUserId(u.id)}
                  >
                    <td>
                      <div className="al-admin-user-cell">
                        <Avatar
                          user={{
                            initials: initialsOf(u.name, u.email),
                            avatar_color: colorFor(u.email),
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div className="al-admin-user-name">
                            {u.name || "—"}
                            {u.is_admin && <span className="al-admin-pill">admin</span>}
                          </div>
                          <div className="tiny muted">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-mobile">
                      <span className="chip">{tierLabel(u.plan_tier)}</span>
                    </td>
                    <td className="hide-mobile"><b>{u.plans_count}</b></td>
                    <td className="hide-mobile">{formatDate(u.created_at)}</td>
                    <td className="hide-mobile">{u.last_payment_at ? relativeTime(u.last_payment_at) : "—"}</td>
                    <td className="hide-mobile">
                      <span className={"al-admin-dot " + (u.is_active ? "is-on" : "is-off")} title={u.is_active ? "Activo" : "Inactivo"} />
                      {u.is_active ? "Activo" : "Inactivo"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Icon name="arrow" size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="al-admin-pager">
              <div className="tiny muted">
                Página {page + 1} · mostrando {visible.length} de {users?.length || 0} cargados
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                >
                  <Icon name="arrowL" size={12} /> Anterior
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNextPage || loading}
                >
                  Siguiente <Icon name="arrow" size={12} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {openUserId != null && (
        <UserDetailModal
          userId={openUserId}
          onClose={() => setOpenUserId(null)}
          onUpdated={(u) => {
            // Reflect change inline en el listado sin re-fetch full.
            setUsers((prev) =>
              prev
                ? prev.map((row) =>
                    row.id === u.id
                      ? { ...row, plan_tier: u.plan_tier, is_admin: u.is_admin, is_active: u.is_active }
                      : row,
                  )
                : prev,
            );
          }}
        />
      )}
    </div>
  );
}

// ============= Modal detalle =============

interface UserDetailModalProps {
  userId: number;
  onClose: () => void;
  onUpdated: (u: AdminUserSummary) => void;
}

function UserDetailModal({ userId, onClose, onUpdated }: UserDetailModalProps) {
  const toast = useToast();
  const { user: me } = useAuth();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getUser(userId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const updateField = useCallback(
    async (data: Partial<AdminUserDetail>) => {
      if (!detail || saving) return;
      setSaving(true);
      try {
        const updated = await patchUser(detail.id, {
          plan_tier: data.plan_tier,
          is_admin: data.is_admin,
          is_active: data.is_active,
        });
        setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
        onUpdated(updated);
        toast("Cambios guardados");
      } catch (e) {
        toast("No pude guardar: " + (e instanceof Error ? e.message : ""));
      } finally {
        setSaving(false);
      }
    },
    [detail, onUpdated, saving, toast],
  );

  const onToggleAdmin = () => {
    if (!detail) return;
    const becoming = !detail.is_admin;
    const action = becoming ? "promover a admin" : "quitar permisos de admin";
    if (!window.confirm(`¿${action} a ${detail.email}?`)) return;
    updateField({ is_admin: becoming });
  };

  const onToggleActive = () => {
    if (!detail) return;
    const becoming = !detail.is_active;
    const action = becoming ? "reactivar" : "desactivar";
    if (!window.confirm(`¿${action} la cuenta de ${detail.email}?`)) return;
    updateField({ is_active: becoming });
  };

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div className="al-modal al-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="card-header">
          <div className="card-title">Detalle del coach</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton height={48} /><Skeleton height={120} /><Skeleton height={80} />
          </div>
        ) : err || !detail ? (
          <EmptyState
            icon="user"
            title="No pude cargar el detalle"
            body={err || "Usuario no encontrado"}
          />
        ) : (
          <>
            {/* Identidad */}
            <div className="al-admin-detail-head">
              <Avatar
                user={{
                  initials: initialsOf(detail.name, detail.email),
                  avatar_color: colorFor(detail.email),
                }}
                size="lg"
              />
              <div style={{ minWidth: 0 }}>
                <div className="al-admin-detail-name">{detail.name || "—"}</div>
                <div className="tiny muted">{detail.email}</div>
                <div className="tiny muted">ID #{detail.id}</div>
              </div>
            </div>

            {/* Métricas */}
            <div className="al-admin-detail-grid">
              <Stat label="Planes" value={String(detail.plans_count)} />
              <Stat label="Pagos" value={String(detail.payments.length)} />
              <Stat
                label="Últ. pago"
                value={detail.last_payment_at ? relativeTime(detail.last_payment_at) : "Nunca"}
              />
              <Stat label="Registrado" value={formatDate(detail.created_at)} />
            </div>

            {/* Datos extra */}
            {(detail.instagram || detail.web || detail.phone) && (
              <div className="al-admin-detail-extra">
                {detail.instagram && (
                  <div><span className="muted tiny">IG:</span> @{detail.instagram}</div>
                )}
                {detail.web && (
                  <div><span className="muted tiny">Web:</span> {detail.web}</div>
                )}
                {detail.phone && (
                  <div><span className="muted tiny">Tel:</span> {detail.phone}</div>
                )}
              </div>
            )}

            {/* Cambiar tier */}
            <div className="field">
              <label>Tier</label>
              <select
                value={detail.plan_tier}
                disabled={saving}
                onChange={(e) => updateField({ plan_tier: e.target.value })}
              >
                <option value="free">Free</option>
                <option value="solo">Solo</option>
                <option value="pro">Pro</option>
                <option value="studio">Studio</option>
              </select>
            </div>

            {/* Toggles */}
            <div className="al-admin-detail-actions">
              <button
                className={"btn " + (detail.is_admin ? "btn-secondary" : "btn-ghost") + " btn-sm"}
                onClick={onToggleAdmin}
                disabled={saving || (detail.id === me?.id && detail.is_admin)}
                title={detail.id === me?.id && detail.is_admin ? "No podés quitarte tu propio admin" : undefined}
              >
                <Icon name="settings" size={12} />
                {detail.is_admin ? "Es admin" : "Hacer admin"}
              </button>
              <button
                className={"btn " + (detail.is_active ? "btn-secondary" : "btn-ghost") + " btn-sm"}
                onClick={onToggleActive}
                disabled={saving}
              >
                <Icon name={detail.is_active ? "check" : "x"} size={12} />
                {detail.is_active ? "Activo" : "Inactivo"}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => toast("Forzar logout no está implementado todavía")}
              >
                <Icon name="logout" size={12} /> Cerrar sesión
              </button>
              <a
                className="btn btn-ghost btn-sm"
                href={`mailto:contacto@nilabs.co?subject=Exportar%20planes%20de%20${encodeURIComponent(detail.email)}&body=Solicitar%20snapshot%20completo%20del%20coach%20${detail.id}`}
              >
                <Icon name="upload" size={12} /> Exportar planes
              </a>
            </div>

            {/* Últimos pagos (top 5) */}
            <div>
              <div className="card-title" style={{ marginBottom: 6 }}>Últimos pagos</div>
              {detail.payments.length === 0 ? (
                <div className="muted tiny">Sin pagos registrados.</div>
              ) : (
                <ul className="al-admin-payment-list">
                  {detail.payments.slice(0, 5).map((p) => (
                    <li key={p.id}>
                      <span>{formatDate(p.created_at)}</span>
                      <span className="chip">{tierLabel(p.plan_tier)}</span>
                      <b>${p.amount_cop.toLocaleString("es-CO")}</b>
                      {p.method && <span className="muted tiny">· {p.method}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="tiny muted">
              Última actualización: {formatDateTime(detail.updated_at)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="al-admin-stat">
      <div className="al-admin-stat-label">{label}</div>
      <div className="al-admin-stat-value">{value}</div>
    </div>
  );
}
